/**
 * Virtual Pointer Events System
 *
 * Provides a custom hover/click detection system for WebGL canvas elements.
 * Since Three.js/uikit elements are rendered to an offscreen texture,
 * this system uses R3F raycasting on the display mesh and converts
 * UV coordinates to screen coordinates for hit detection.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useId,
  useRef,
  useSyncExternalStore,
} from "react";

export interface Bounds {
  x: number;
  y: number;
  width: number;
  height: number;
}

// Internal store for hover zones and their listeners
interface VirtualPointerStore {
  zones: Map<string, Bounds>;
  listeners: Map<string, Set<(hovered: boolean) => void>>;
  clickHandlers: Map<string, () => void>;
  hoveredZones: Set<string>;
  mouseX: number;
  mouseY: number;
  screenWidth: number;
  screenHeight: number;
}

/**
 * Context value for virtual pointer events (raw API)
 */
export interface VirtualPointerEventsContextValue {
  mouseX: number;
  mouseY: number;
  canvasContainerRef: React.RefObject<HTMLDivElement>;
  registerZone: (id: string, bounds: Bounds) => void;
  unregisterZone: (id: string) => void;
  isHovered: (id: string) => boolean;
  subscribe: (id: string, callback: (hovered: boolean) => void) => () => void;
  registerClickHandler: (id: string, handler: () => void) => void;
  unregisterClickHandler: (id: string) => void;
  /** Update mouse position and notify hover listeners */
  updateMousePosition: (x: number, y: number) => void;
  /** Handle click at position, triggering matching click handlers */
  onClick: (x: number, y: number) => void;
  /** Update from UV coordinates (0-1 range, for R3F pointer events) */
  updateFromUV: (u: number, v: number) => void;
  /** Handle click from UV coordinates */
  onClickFromUV: (u: number, v: number) => void;
  /** Set screen dimensions for UV to pixel conversion */
  setScreenDimensions: (width: number, height: number) => void;
  /** Clear hover state (when pointer leaves the mesh) */
  clearHover: () => void;
  onMouseMove: (e: React.MouseEvent<HTMLDivElement>) => void;
  onMouseLeave: () => void;
}

// Helper function (defined early for use in createDefault)
function isPointInBounds(x: number, y: number, bounds: Bounds): boolean {
  return (
    x >= bounds.x &&
    x <= bounds.x + bounds.width &&
    y >= bounds.y &&
    y <= bounds.y + bounds.height
  );
}

const VirtualPointerEventsContextImpl =
  createContext<VirtualPointerEventsContextValue | null>(null);

export const VirtualPointerEventsContext = Object.assign(
  VirtualPointerEventsContextImpl,
  {
    useStore: (): VirtualPointerStore => {
      const store = useRef<VirtualPointerStore>({
        zones: new Map(),
        listeners: new Map(),
        clickHandlers: new Map(),
        hoveredZones: new Set(),
        mouseX: -9999,
        mouseY: -9999,
        screenWidth: 1920,
        screenHeight: 1080,
      });

      return store.current;
    },
    createContextValue: (
      store: VirtualPointerStore,
      mousePos: { x: number; y: number },
      canvasContainerRef: React.RefObject<HTMLDivElement>,
    ): Omit<
      VirtualPointerEventsContextValue,
      "onMouseMove" | "onMouseLeave"
    > => {
      // Convert UV (0-1) to screen pixel coordinates
      const uvToPixel = (u: number, v: number): { x: number; y: number } => {
        // UV origin is bottom-left in Three.js, we need top-left
        // u = 0-1 left to right, v = 0-1 bottom to top
        return {
          x: u * store.screenWidth,
          y: (1 - v) * store.screenHeight, // Flip Y
        };
      };

      const updateHoverState = (x: number, y: number) => {
        store.mouseX = x;
        store.mouseY = y;

        const newHovered = new Set<string>();

        for (const [id, bounds] of store.zones) {
          if (isPointInBounds(x, y, bounds)) {
            newHovered.add(id);
          }
        }

        for (const [id, listenerSet] of store.listeners) {
          const wasHovered = store.hoveredZones.has(id);
          const isHovered = newHovered.has(id);

          if (wasHovered !== isHovered) {
            for (const listener of listenerSet) {
              listener(isHovered);
            }
          }
        }

        store.hoveredZones = newHovered;
      };

      const handleClick = (x: number, y: number) => {
        for (const [id, bounds] of store.zones) {
          if (isPointInBounds(x, y, bounds)) {
            const handler = store.clickHandlers.get(id);
            if (handler) {
              handler();
              return;
            }
          }
        }
      };

      return {
        canvasContainerRef,
        mouseX: mousePos.x,
        mouseY: mousePos.y,
        registerZone: (id: string, bounds: Bounds) => {
          store.zones.set(id, bounds);
          if (isPointInBounds(store.mouseX, store.mouseY, bounds)) {
            store.hoveredZones.add(id);
            const listeners = store.listeners.get(id);
            if (listeners) {
              for (const listener of listeners) {
                listener(true);
              }
            }
          }
        },
        unregisterZone: (id: string) => {
          store.zones.delete(id);
          store.hoveredZones.delete(id);
          store.clickHandlers.delete(id);
        },
        isHovered: (id: string) => store.hoveredZones.has(id),
        subscribe: (id: string, callback: (hovered: boolean) => void) => {
          if (!store.listeners.has(id)) {
            store.listeners.set(id, new Set());
          }
          store.listeners.get(id)?.add(callback);

          return () => {
            const listeners = store.listeners.get(id);
            if (listeners) {
              listeners.delete(callback);
              if (listeners.size === 0) {
                store.listeners.delete(id);
              }
            }
          };
        },
        registerClickHandler: (id: string, handler: () => void) => {
          store.clickHandlers.set(id, handler);
        },
        unregisterClickHandler: (id: string) => {
          store.clickHandlers.delete(id);
        },
        updateMousePosition: updateHoverState,
        onClick: handleClick,
        // New UV-based methods for R3F pointer events
        updateFromUV: (u: number, v: number) => {
          const { x, y } = uvToPixel(u, v);
          updateHoverState(x, y);
        },
        onClickFromUV: (u: number, v: number) => {
          const { x, y } = uvToPixel(u, v);
          handleClick(x, y);
        },
        setScreenDimensions: (width: number, height: number) => {
          store.screenWidth = width;
          store.screenHeight = height;
        },
        clearHover: () => {
          // Notify all currently hovered zones they are no longer hovered
          for (const [id, listenerSet] of store.listeners) {
            if (store.hoveredZones.has(id)) {
              for (const listener of listenerSet) {
                listener(false);
              }
            }
          }
          store.hoveredZones.clear();
          store.mouseX = -9999;
          store.mouseY = -9999;
        },
      };
    },
  },
);

/**
 * Raw hook to access the virtual pointer events context
 * Use this for advanced multi-zone scenarios (like menus with multiple items)
 *
 * @example
 * ```tsx
 * const pointer = useVirtualPointerEvents();
 * pointer.registerZone('zone-1', bounds1);
 * pointer.registerClickHandler('zone-1', onClick);
 * ```
 */
export function useVirtualPointerEvents() {
  const context = useContext(VirtualPointerEventsContext);
  if (!context) {
    throw new Error("useVirtualPointerEvents must be used within a CRTScreen");
  }
  return context;
}

// =============================================================================
// Smart Hook API - Automatic ID management
// =============================================================================

interface UseVirtualPointerZoneOptions {
  /** Bounds for the interactive zone. Pass null to disable. */
  bounds: Bounds | null;
  /** Called when the zone is clicked */
  onClick?: () => void;
  /** Called when hover state changes */
  onHoverChange?: (isHovered: boolean) => void;
}

interface UseVirtualPointerZoneResult {
  /** Whether the zone is currently hovered */
  isHovered: boolean;
  /** The auto-generated zone ID (useful for debugging) */
  zoneId: string;
}

/**
 * Smart hook for single-zone pointer interactions
 * Automatically generates ID, handles registration, and cleanup
 *
 * @example
 * ```tsx
 * const { isHovered } = useVirtualPointerZone({
 *   bounds: { x: 100, y: 100, width: 200, height: 50 },
 *   onClick: () => console.log('clicked!'),
 * });
 * ```
 */
export function useVirtualPointerZone({
  bounds,
  onClick,
  onHoverChange,
}: UseVirtualPointerZoneOptions): UseVirtualPointerZoneResult {
  const pointer = useVirtualPointerEvents();
  const zoneId = useId();
  const onClickRef = useRef(onClick);
  const onHoverChangeRef = useRef(onHoverChange);

  // Keep refs up to date
  onClickRef.current = onClick;
  onHoverChangeRef.current = onHoverChange;

  // Register/update bounds
  useEffect(() => {
    if (bounds) {
      pointer.registerZone(zoneId, bounds);
    }
    return () => {
      pointer.unregisterZone(zoneId);
    };
  }, [pointer, zoneId, bounds?.x, bounds?.y, bounds?.width, bounds?.height]);

  // Register click handler
  useEffect(() => {
    if (onClick) {
      pointer.registerClickHandler(zoneId, () => onClickRef.current?.());
    }
    return () => {
      pointer.unregisterClickHandler(zoneId);
    };
  }, [pointer, zoneId, !!onClick]);

  // Subscribe to hover changes
  const subscribe = useCallback(
    (callback: () => void) => {
      return pointer.subscribe(zoneId, (hovered) => {
        onHoverChangeRef.current?.(hovered);
        callback();
      });
    },
    [pointer, zoneId],
  );

  const getSnapshot = useCallback(() => {
    return pointer.isHovered(zoneId);
  }, [pointer, zoneId]);

  const isHovered = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);

  return { isHovered, zoneId };
}

// =============================================================================
// Multi-zone Hook - For components managing multiple zones (menus, lists)
// =============================================================================

interface UseVirtualPointerZonesOptions<T> {
  /** Array of items to create zones for */
  items: T[];
  /** Function to generate bounds for each item */
  getBounds: (item: T, index: number) => Bounds;
  /** Called when an item is clicked */
  onItemClick?: (item: T, index: number) => void;
  /** Prefix for zone IDs (useful for debugging) */
  idPrefix?: string;
}

interface UseVirtualPointerZonesResult {
  /** Check if a specific index is hovered */
  isHovered: (index: number) => boolean;
  /** Get the zone ID for a specific index */
  getZoneId: (index: number) => string;
}

/**
 * Hook for managing multiple pointer zones (like menu items)
 * Handles registration and cleanup for all zones automatically
 *
 * @example
 * ```tsx
 * const { isHovered } = useVirtualPointerZones({
 *   items: menuItems,
 *   getBounds: (item, index) => ({
 *     x: 100, y: 50 + index * 60,
 *     width: 200, height: 50
 *   }),
 *   onItemClick: (item, index) => item.action(),
 * });
 * ```
 */
export function useVirtualPointerZones<T>({
  items,
  getBounds,
  onItemClick,
  idPrefix = "zone",
}: UseVirtualPointerZonesOptions<T>): UseVirtualPointerZonesResult {
  const pointer = useVirtualPointerEvents();
  const baseId = useId();
  const onItemClickRef = useRef(onItemClick);
  onItemClickRef.current = onItemClick;

  const getZoneId = useCallback(
    (index: number) => `${idPrefix}-${baseId}-${index}`,
    [idPrefix, baseId],
  );

  // Register all zones
  useEffect(() => {
    items.forEach((item, index) => {
      const zoneId = getZoneId(index);
      const bounds = getBounds(item, index);
      pointer.registerZone(zoneId, bounds);

      if (onItemClick) {
        pointer.registerClickHandler(zoneId, () => {
          onItemClickRef.current?.(item, index);
        });
      }
    });

    return () => {
      items.forEach((_, index) => {
        const zoneId = getZoneId(index);
        pointer.unregisterZone(zoneId);
        pointer.unregisterClickHandler(zoneId);
      });
    };
  }, [pointer, items.length, getBounds, getZoneId, !!onItemClick]);

  const isHovered = useCallback(
    (index: number) => pointer.isHovered(getZoneId(index)),
    [pointer, getZoneId],
  );

  return { isHovered, getZoneId };
}
